import React, { useState, useEffect, useCallback } from 'react';

import classnames from 'classnames';
import groupBy from 'lodash.groupby';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { testId } from 'ui-components/services/testid-service';

import { AutoCompleteField } from '../../../../constants/autocomplete-field';
import { useUniqueHtmlId } from '../../../../hooks/use-unique-html-id';
import Global from '../../../../store/global-selectors';
import { FIELD_NAMES } from '../../constants';
import { useAddressField, useAddressFormContext } from '../../context';
import { optionalFieldsByCountryCode } from '../../address-schema';

import style from './style.module.less';

const elementId = 'state-dropdown';

export const StateDropdown = () => {
  const { value, onChange, error, labelDescriptor, address, onBlur, trackInputClick, isRequired } = useAddressField(FIELD_NAMES.STATE);
  const { showAllErrors, disabled, overrideRequiredFields } = useAddressFormContext();
  const { countryCode } = address;

  const [touched, setTouched] = useState(false);
  const [availableStates, setAvailableStates] = useState([]);
  const [statesByCountry, setStatesByCountry] = useState({});
  const id = useUniqueHtmlId(elementId);
  const autoFillId = useUniqueHtmlId('address-level1');
  const countryData = useSelector(Global.selectReferenceDatagetStates);
  const onClick = useCallback(() => trackInputClick(elementId), [trackInputClick]);

  const _onBlur = useCallback(
    (v) => {
      setTouched(true);
      onBlur(v);
    },
    [onBlur, setTouched],
  );

  const _onChange = useCallback(
    (event) => {
      const target = event?.target;
      const targetValue = target?.value;

      const targetState =
        targetValue.length === 2
          ? availableStates.find((state) => state.code === targetValue)
          : availableStates.find((state) => state.name === targetValue);

      if (targetState) {
        onChange(targetState.code);
        return;
      }
      onChange('');
    },
    [onChange, availableStates],
  );

  useEffect(() => {
    setStatesByCountry(groupBy(countryData, 'countryCode'));
  }, [countryData]);

  useEffect(() => {
    const states = statesByCountry[countryCode] || [];
    const stateValueIsFromDifferentCountry = value && !states.find((state) => state.code === value);
    if (states.length && stateValueIsFromDifferentCountry) {
      onChange();
    }
    setAvailableStates(states);
  }, [statesByCountry, countryCode, value, onChange]);

  const errorMessage = touched || showAllErrors ? error : null;

  // If overrideRequiredFields is provided, use context value; otherwise use country-based logic
  const optionalFieldsForCountry = optionalFieldsByCountryCode[countryCode] || [];
  const isStateRequiredByCountry = !optionalFieldsForCountry.includes(FIELD_NAMES.STATE);
  const isStateRequired = overrideRequiredFields !== null ? isRequired : isStateRequiredByCountry;

  return (
    <div className={style.container} {...testId('address-state-dropdown')}>
      <label className={classnames(style.label, { [style.error]: !!errorMessage })} htmlFor={id}>
        <FormattedMessage {...labelDescriptor} />
        {isStateRequired && <span className={style['required-asterisk']}>*</span>}
      </label>
      <DropdownMenu
        className={style['dropdown-menu']}
        disabled={disabled}
        dropdownMenuShouldHaveWidthOfButton
        id={id}
        inlineMessage={errorMessage?.message}
        inlineMessageTooltip={errorMessage?.tooltip}
        inlineMessageType={errorMessage && INLINE_MESSAGE_TYPES.error}
        onBlur={() => _onBlur(value)}
        variant='form'
      >
        <DropdownMenuToggler className={style.toggler} onClick={onClick} tabIndex={-1} testId='address-state-dropdown-button' theme='white'>
          {/*
        The data-recurly prop is necessary here to identify non card billing fields for Recurly.js docs here  https://developers.recurly.com/reference/recurly-js/#build-a-payment-form-with-elements
        This input is also necessary for Autofill - https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#attr-fe-autocomplete
        */}
          <input
            autoComplete={AutoCompleteField.AddressLevel1}
            className={style['state-input']}
            data-private
            data-recurly='state'
            id={autoFillId}
            name={FIELD_NAMES.STATE}
            onChange={_onChange}
            value={value}
          />
        </DropdownMenuToggler>
        {availableStates.map(({ code }) => (
          <DropdownMenuItem key={code} onClick={() => onChange(code)} selected={value === code}>
            {code}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    </div>
  );
};
